/**
 * Panel «Restringir acceso» de UNA carpeta (spec 2026-10-07 §3). Reemplaza al
 * drawer «Editar permisos» de carpeta. Lo abre quien tiene rango ≥ 60
 * (list_folders decide el botón); la regla de Firestore lo exige también.
 * La lógica vive en lib/restrict_panel (pura, con tests).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { Drawer, Button, Checkbox, List, Alert, Modal, Tag, notification } from 'antd';

import { restrictions as restrictionsActions } from '../../actions/database/restrictions';
import { loadTargetRole } from '../../lib/role_mirror';
import { useDocuments } from './context';
import { getChildCollection } from './folder_ref';
import { collectionPathOfEntry } from '../../lib/folder_chain';
import { ownRestriction } from '../../lib/folder_restrictions';
import { countSubtreeFolders } from '../../lib/folder_tree';
import { canRestrict } from '../../lib/rank';
import { namesByPath, restrictRows, restrictDiff, confirmText } from '../../lib/restrict_panel';

export const RestrictAccessDrawer = () => {
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const globalRole = useSelector(state => state.get('roles').get('roles').roles);
  const { folders } = useDocuments();
  const { permissions } = folders.current.user;
  const cid = folders.current.company.data.cid;
  const folder = useMemo(() => permissions.folder || {}, [permissions.folder]);
  const collectionPath = folder.reference ? collectionPathOfEntry(folder.reference) : null;
  const access = permissions.visible ? folders.current.access() : null;
  const restrictions = useMemo(() => (access ? access.restrictions : []), [access]);
  const readError = !access || access.restrictionsState === 'error';

  const [rolesByUid, setRolesByUid] = useState(new Map());
  const [checked, setChecked] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Al abrir: casillas desde el doc de ESTA carpeta y roles de los vinculados
  // (para no listar a root; si un rol no se puede leer, la fila se muestra).
  useEffect(() => {
    if (!permissions.visible) return undefined;
    const own = ownRestriction(collectionPath, restrictions);
    setChecked(new Set(own ? own.uids : []));
    const db = firebase.firestore();
    const uids = [...new Set((permissions.users || []).map(u => u?.reference?.id).filter(Boolean))];
    let alive = true;
    Promise.all(uids.map(uid => loadTargetRole({ db, uid }).then(r => [uid, r], () => [uid, null])))
      .then(pairs => { if (alive) setRolesByUid(new Map(pairs)); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.visible, collectionPath]);

  const names = useMemo(() => namesByPath({ routes: folders.routes.data, folder }), [folders.routes.data, folder]);
  const rows = useMemo(
    () => restrictRows({ users: permissions.users, collectionPath, restrictions, rolesByUid, names }),
    [permissions.users, collectionPath, restrictions, rolesByUid, names],
  );

  const onClose = () => {
    setChecked(new Set());
    setRolesByUid(new Map());
    permissions.set({ visible: false, users: permissions.users, folder: {} });
  };

  const toggle = (uid, on) => setChecked(prev => {
    const next = new Set(prev);
    if (on) next.add(uid); else next.delete(uid);
    return next;
  });

  const write = async ({ addedUids, removedUids }) => {
    setSaving(true);
    try {
      await dispatch(restrictionsActions.set({ editorRole: globalRole, cid, collectionPath, addedUids, removedUids }));
      notification.success({ message: 'Restringir acceso', description: 'Se guardaron las restricciones de la carpeta.', placement: 'topLeft' });
      await folders.current.reloadAccess();
      folders.set({ ...folders.data, isLoad: true });
      onClose();
    } catch (e) {
      notification.error({ message: 'Restringir acceso', description: (e && e.message) || 'No se pudieron guardar las restricciones.', placement: 'topLeft' });
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (readError || !collectionPath) return;
    const plan = restrictDiff({ rows, checked, collectionPath, restrictions });
    if (!plan.changed) { onClose(); return; }
    if (!plan.added.length) { await write(plan); return; }
    setSaving(true);
    let n = null;
    try {
      const coll = getChildCollection(firebase, folder.reference);
      n = coll ? await countSubtreeFolders({ parentDoc: coll.parent, collectionId: coll.id }) : null;
    } catch (e) {
      n = null;
    }
    setSaving(false);
    Modal.confirm({
      title: `Restringir acceso a ${folder.name || 'la carpeta'}`,
      content: confirmText({ added: plan.added, subfolders: n }),
      okText: 'Restringir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => write(plan),
    });
  };

  if (!permissions.visible) return null;

  return (
    <Drawer
      title={`Restringir acceso — ${folder.name || ''}`}
      width={560}
      placement="left"
      visible
      maskClosable={false}
      onClose={onClose}
      footer={(
        <div style={{ textAlign: 'right' }}>
          <Button style={{ marginRight: 8 }} onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button type="primary" loading={saving} disabled={readError || !collectionPath || !canRestrict(globalRole)} onClick={onSave}>Guardar</Button>
        </div>
      )}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Un usuario restringido deja de ver esta carpeta, sus archivos y todas sus subcarpetas: en el listado, en la descarga y en la búsqueda. Lo demás de la empresa no cambia."
      />
      {readError ?
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
          message="No se pudieron leer las restricciones de esta empresa. Cierre, use «Actualizar» e intente de nuevo; no se guardará nada hasta poder leerlas." /> : null}
      {!collectionPath ?
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message="La carpeta no tiene una ruta válida." /> : null}
      <List
        size="small"
        bordered
        locale={{ emptyText: 'La empresa no tiene usuarios vinculados.' }}
        dataSource={rows}
        renderItem={row => (
          <List.Item key={row.uid}>
            <Checkbox
              checked={row.lockedFrom ? true : checked.has(row.uid)}
              disabled={!!row.lockedFrom || readError || saving}
              onChange={e => toggle(row.uid, e.target.checked)}
            >
              Restringido
            </Checkbox>
            <span style={{ marginLeft: 8, flex: 1 }}>{row.email}</span>
            {row.lockedFrom ? <Tag>{`Restringido desde ${row.lockedFrom}`}</Tag> : null}
          </List.Item>
        )}
      />
    </Drawer>
  );
};

export default RestrictAccessDrawer;
